import {
  SubmissionFileType,
  SubmissionFileState,
  ReviewerType,
  ReviewerAnonymisationType,
  TransparentPeerReviewType,
  Author,
  Reviewer,
  SubmissionFile,
  FileSizeValidationInput,
  DasOption,
} from '@/types'
import { defineRule } from 'vee-validate'
import { authorValidate } from './services/validation/authorInfo'
import store from './store'
import stripHtml from './utils/stripHtml'
import limits from './constants/limits'

defineRule('at-least-one-author-required', (value: string) => {
  if (value.length > 0) {
    return true
  }
  return 'At least one author must be provided'
})

defineRule('manuscript-file-required', (value: SubmissionFile[]) => {
  if (Array.isArray(value) && value.some((file: SubmissionFile) =>
    file.type === SubmissionFileType.MainArticle
    && file.state === SubmissionFileState.Complete,
  )) {
    return true
  }
  return 'A manuscript file must be uploaded'
})

defineRule('cover-letter-required', (value: SubmissionFile[]) => {
  if (Array.isArray(value) && value.some((file: SubmissionFile) =>
    file.type === SubmissionFileType.CoverLetter
    && file.state === SubmissionFileState.Complete,
  )) {
    return true
  }
  return 'A cover letter file must be uploaded'
})

defineRule('themed-collection-option-required', () => {
  const response = store.state.activeSubmission.themedCollectionResponse
  const title = store.state.activeSubmission.themedCollectionTitle
  if (response === '') {
    return 'You must select an option for themed collection'
  }
  if (response === 'yes' && title === '') {
    return 'You must add the title of the themed collection'
  }
  return true
})

defineRule('data-availability-statement-option-required', () => {
  const {
    dasCustomContent,
    dasReview,
    dasCustomSelected,
    selectedOption,
    dasCustomIsSent,
  } = store.state.das

  const files = store.state.activeSubmission.files || []

  const plainText = (dasCustomContent || '').replace(/<[^>]*>/g, '').trim()

  const hasCompleteFile = Array.isArray(files) && files.some(
    f => f.type === SubmissionFileType.DAS && f.state === SubmissionFileState.Complete,
  )
  if (hasCompleteFile) return true
  if (dasReview) return true
  if (dasCustomSelected) {
    if (plainText.length > limits.DAS.characters.statement) {
      return `Data Availability Statement must not exceed ${limits.DAS.characters.statement} characters`
    }
    if (!plainText) {
      return 'You must enter your Data Availability Statement'
    }
    if (!dasCustomIsSent) {
      return 'You must click \'Add Text\' to save your Data Availability Statement'
    }
    return true
  }
  if (selectedOption === DasOption.File) {
    return 'Data Availability Statement must be provided'
  }
  return 'Data Availability Statement must be provided'
})

defineRule('file-size', (value: FileSizeValidationInput) => {
  const fileList = value[0]
  const file = fileList[0]
  let copyOfActiveSubmissionFiles = [...store.state.activeSubmission.files]
  const groupedFiles = store.getters.groupedSubmissionFiles

  const INDIVIDUAL_FILE_SIZE_LIMIT = 80_000_000 // 80MB
  const TOTAL_FILE_SIZE_LIMIT = 800_000_000 // 800MB

  if (
    store.state.das.selectedOption !== DasOption.File
    && groupedFiles?.some((file: { type: SubmissionFileType }) => file.type === SubmissionFileType.DAS)
  ) {
    copyOfActiveSubmissionFiles = copyOfActiveSubmissionFiles.filter(
      file => file.type !== SubmissionFileType.DAS,
    )
  }

  if (file.size > INDIVIDUAL_FILE_SIZE_LIMIT) {
    return `${file.name}: File exceeds the maximum single file size of 80MB.`
  }

  const totalSize = [...fileList, ...copyOfActiveSubmissionFiles].reduce((sum, f) => sum + f.size, 0)

  if (totalSize > TOTAL_FILE_SIZE_LIMIT) {
    return `${file.name}: Upload would exceed your 800MB total file limit.`
  }

  return true
})

defineRule('file-type', (value: [File[], string]) => {
  var fileValue = value[0][0] as File
  const unacceptableTypes = ['.exe', '.shs', '.com', '.vbs', '.dll', '.docm']
  const IsValid = unacceptableTypes.every(ext => !fileValue.name.toLowerCase().endsWith(ext))
  if (IsValid) return true
  return `${fileValue.name}: Invalid file format, please use a different format.`
})

defineRule('file-duplicates', (value: FileList) => {
  if (store.state.activeSubmission.files.length > 0) {
    var filenames: string[] = store.state.activeSubmission.files.map(x => x.file.toLowerCase())
    var isFileNameAlreadyExists = filenames.indexOf(value[0].name.toLowerCase()) > -1
    if (isFileNameAlreadyExists) {
      return `${value[0].name}: You have already uploaded a file with this name. Please choose another file to upload.`
    }
  }
  return true
})

const commonValidationForRequiredString: (value: string) => boolean = (value: string) => {
  const cleanedValue = value.replace(/<p>\s*<\/p>/g, '')
  return cleanedValue.trim().length > 0
}

defineRule('article-title-required', (value: string) => {
  if (!commonValidationForRequiredString(value)) {
    return 'You must enter an article title'
  }
  return true
})

defineRule('max-words', (value: string, [max, label]: [number, string?]) => {
  if (value) {
    const wordCount = value.trim().split(/\s+/).filter(Boolean).length
    if (wordCount > max) {
      return `Your ${label} must not exceed ${max} words`
    }
  }
  return true
})

defineRule('article-abstract-required', (value: string) => {
  if (!commonValidationForRequiredString(value)) {
    return 'You must enter an abstract'
  }
  return true
})

defineRule('at-least-two-recommended-reviewers-required', (value: Reviewer[]) => {
  const minRecommended = limits.reviewers.counts.minRecommended
  if (value.filter(item => item.type === ReviewerType.Recommend).length >= minRecommended) {
    return true
  }
  return 'You must add a minimum of two recommended reviewers'
})

defineRule('anonymised-peer-review-option-required', (value: ReviewerAnonymisationType) => {
  if (value === ReviewerAnonymisationType.DoubleAnonymised
    || value === ReviewerAnonymisationType.SingleAnonymised) {
    return true
  }
  return 'You must select an option for single or double anonymised peer review'
})

defineRule('at-least-two-associate-editors-required', (value: string) => {
  if (value.length === 2) {
    return true
  }
  return 'You must select two Associate Editors'
})

document.addEventListener('click', function (event) {
  const target = event.target as HTMLElement
  if (target.matches('.scroll-link')) {
    event.preventDefault()
    const id = target.getAttribute('data-target-id')
    const el = id ? document.getElementById(id) : null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
})

defineRule(
  'required',
  (value: string | number | null | undefined, [customMessage]: [string?]) => {
    if (typeof value === 'string' && value.trim().length > 0) {
      return true
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return true
    }
    return customMessage || 'This field is required'
  },
)

defineRule('ccdc-deposition-number-required', (value: string) => {
  if (!commonValidationForRequiredString(value)) {
    return 'You must enter at least one CCDC deposition number'
  }
  return true
})

defineRule('terms-and-conditions-required', (value: boolean) => {
  if (value) {
    return true
  }
  return 'Please confirm you have read and agree to the terms and conditions and acknowledge the article processing charge (APC) associated with publishing in this journal'
})

defineRule('transparent-peer-review-option-required', (value: TransparentPeerReviewType) => {
  if (value !== TransparentPeerReviewType.Unknown) {
    return true
  }
  return 'You must select an option for transparent peer review'
})

defineRule('no-failed-uploads', (value: SubmissionFile[]) => {
  if (
    value.every(
      (file: SubmissionFile) =>
        file.state !== SubmissionFileState.Failure
        && file.state !== SubmissionFileState.FailureDuplicate,
    )
  ) {
    return true
  }
  return 'All file uploads must be successful'
})

defineRule('all-authors-must-be-valid', (value: Author[]) => {
  return value.every(authorValidate) || 'All authors must be valid'
})

defineRule('not-duplicated-mail-edit-author', (value: string, [currentAuthorId]: [string?]) => {
  const valueToLowerCase = value.toLowerCase()
  const listCopy = store.state.activeSubmission.authors.filter((a: Author) => a.id != currentAuthorId)

  if (listCopy.every((au: { email: string }) => au.email.toLowerCase() !== valueToLowerCase)) {
    return true
  }

  return 'There is already an author with this email address added to this submission.'
})

defineRule('email', (value: string) => {
  const regex = /^(?!\.)(?!.*\.\.)[A-Z0-9._%+-]+(?<!\.)@[A-Z0-9.-]+\.[A-Z]{2,}$/i
  if (!regex.test(value)) {
    return 'You must enter a valid email'
  }
  return true
})

let lastPushTime = 0
const pushInterval = 3000

defineRule('not-duplicated-mail-edit-reviewer', (value: string, [currentReviewerId]: [string?]) => {
  const currentTime = Date.now()
  const valueToLowerCase = value.toLowerCase()
  const listCopy = store.state.activeSubmission.reviewers.filter((rev: Reviewer) => rev.id != currentReviewerId)

  if (listCopy.every((rev: { email: string }) => rev.email.toLowerCase() !== valueToLowerCase)) {
    if (currentTime - lastPushTime > pushInterval) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'alreadyAddedReviewerEmail',
        eventCategory: 'Form Validation',
        eventAction: 'Email Check',
        alreadyAddedReviewerEmail: 'alreadyAddedReviewerEmail',
      })
      lastPushTime = currentTime
    }
    return true
  }

  if (currentTime - lastPushTime > pushInterval) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'alreadyAddedReviewerEmail',
      eventCategory: 'Form Validation',
      eventAction: 'Email Check',
      alreadyAddedReviewerEmail: 'alreadyAddedReviewerEmail',
    })
    lastPushTime = currentTime
  }

  return 'There is already a reviewer with this email address added to this submission.'
})

defineRule(
  'max-characters-length',
  (value: string, [max, message]: [string | number, string]): string | boolean => {
    if (!value) return true

    const maxNum = typeof max === 'string' ? parseInt(max, 10) : (max as number)
    if (Number.isNaN(maxNum)) return true

    const textOnly = stripHtml(value)

    let msg = message ?? 'You must not exceed'
    msg = msg.replace(/^['"]|['"]$/g, '')

    if (textOnly.length > maxNum) {
      return `${msg} ${maxNum} characters`
    }

    return true
  },
)

defineRule('maximum-number-of-recommended-reviewers-reached', (value: Reviewer[]) => {
  if (!Array.isArray(value)) return true
  return value.length < limits.reviewers.counts.maxRecommended || 'You have reached the maximum number of recommended reviewers'
})

defineRule('themed-collection-max-length', (value: string) => {
  if (!value) return true
  return value.length <= limits.themedCollection.characters.title || `Themed collection title must not exceed ${limits.themedCollection.characters.title} characters`
})
